/**
 * Confirmation Dialog for Role Deletion
 */

import styled from 'styled-components';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Role } from '../../services/api/roleService';
import { AlertTriangle } from 'lucide-react';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  role: Role | null;
  isLoading: boolean;
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const WarningBox = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.status.warningLight};
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const WarningIcon = styled.div`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.status.warning};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const WarningContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const WarningTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const WarningText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

const RoleName = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  role,
  isLoading,
}: ConfirmationDialogProps) => {
  if (!role) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Role"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} isLoading={isLoading}>
            Delete Role
          </Button>
        </>
      }
    >
      <Content>
        <WarningBox>
          <WarningIcon>
            <AlertTriangle />
          </WarningIcon>
          <WarningContent>
            <WarningTitle>Are you sure?</WarningTitle>
            <WarningText>
              You are about to delete the role <RoleName>&quot;{role.name}&quot;</RoleName>. This
              action cannot be undone.
            </WarningText>
            {role.permissions && role.permissions.length > 0 && (
              <WarningText>
                This role has {role.permissions.length} permission
                {role.permissions.length !== 1 ? 's' : ''} assigned.
              </WarningText>
            )}
          </WarningContent>
        </WarningBox>
      </Content>
    </Modal>
  );
};

export default ConfirmationDialog;
